import LinkButton from '@carvana/ds-link-button';
import React, { useCallback } from 'react';
import { Address, StepName } from '|/app/services';
import formatPhone from '|/helpers/phone';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import { ReviewSummaryResponse } from '|/services/purchase-api/types';
import styles from './contact-details.module.css';
import useOrderSummaryVm from '../../use-order-summary-vm';

interface ContactDetailsProps {
  summary: ReviewSummaryResponse | null;
  testId?: string;
}

const ContactDetails: React.FC<ContactDetailsProps> = ({
  summary,
  testId = 'contact-details-section'
}) => {
  const {
    hasPii,
    customerName: name,
    phoneNumber,
    emailAddress,
    homeAddress,
    mailingAddress,
    areHomeAndMailingAddressesTheSame
  } = useOrderSummaryVm(summary);

  const navigate = useNavigateToStep();

  const handleEditClick = useCallback(() => {
    navigate(StepName.PersonalDetails, undefined, 'entry');
  }, [navigate]);

  const hasCompleteHomeAddress =
    homeAddress.line1 && homeAddress.city && homeAddress.state && homeAddress.zipCode;
  const hasCompleteMailingAddress =
    mailingAddress.line1 && mailingAddress.city && mailingAddress.state && mailingAddress.zipCode;

  const formatAddress = (address: Address) => {
    const { line1, line2, city, state, zipCode } = address;
    if (!line1) {
      return {
        firstLine: '',
        secondLine: ''
      };
    }

    const formattedLine2 = line2 ? `, ${line2}` : '';
    const cityStateZip = `${city}, ${state} ${zipCode}`;

    return {
      firstLine: `${line1}${formattedLine2}`,
      secondLine: cityStateZip
    };
  };

  const homeAddressFormatted = formatAddress(homeAddress);
  const mailingAddressFormatted = formatAddress(mailingAddress);
  const formattedPhone = formatPhone(phoneNumber ?? '');

  return (
    <section data-testid={testId}>
      <h3>Contact and registration details</h3>

      <div className={styles.content}>
        {!hasPii && (
          <div className={styles.missingDataMessage}>We still need your personal information</div>
        )}

        {hasPii && (
          <>
            <div className={styles.section}>
              <h4>Register to</h4>
              <div className={styles.sectionContent}>
                <div>{name}</div>
                <div>{formattedPhone ?? phoneNumber}</div>
                <div>{emailAddress}</div>
              </div>
            </div>

            <div className={styles.section} data-testid="pp_contact_home-address">
              <h4>Home address</h4>
              <div className={styles.sectionContent}>
                {!hasCompleteHomeAddress ? (
                  <div className={styles.missingDataMessage}>
                    We still need your home address information
                  </div>
                ) : (
                  <>
                    <div>{homeAddressFormatted.firstLine}</div>
                    <div>{homeAddressFormatted.secondLine}</div>
                  </>
                )}
              </div>
            </div>

            <div className={styles.section} data-testid="pp_contact_mailing-address">
              <h4>Mailing address</h4>
              <div className={styles.sectionContent}>
                {hasCompleteMailingAddress && !areHomeAndMailingAddressesTheSame ? (
                  <>
                    <div>{mailingAddressFormatted.firstLine}</div>
                    <div>{mailingAddressFormatted.secondLine}</div>
                  </>
                ) : (
                  <div>Same as home address</div>
                )}
              </div>
            </div>
          </>
        )}

        <LinkButton
          size="sm"
          underline={false}
          onClick={handleEditClick}
          className={styles.editButton}
          data-analytics-id="pp_review_order_summary_edit_account"
          data-testid="contact-details-edit-button"
        >
          Edit details
        </LinkButton>
      </div>
    </section>
  );
};

export default ContactDetails;
